import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';

const GARDENS_URL =
  'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=05c63d88-cb02-40ec-b6b5-08c3a8958db7';
const PEACOCK =
  'https://www.belvoircastle.com/wp/wp-content/uploads/2026/04/white-peacock.png';
const HERO_IMG =
  'https://BelvoirCastlePull.b-cdn.net/Website/5S5A1084-2%20(1).webp';

const H = {
  ink: '#14101F',
  midnight: '#1C1630',
  torch: '#E07A2F',
  candle: '#C9A227',
  moss: '#3D5C4A',
  bone: '#F6F0E6',
  parchment: '#EDE6D4',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${H.bone};
  padding-top: 158px;
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(380px, 48vh, 560px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 55%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(20, 16, 31, 0.92) 0%,
    rgba(20, 16, 31, 0.45) 38%,
    rgba(20, 16, 31, 0.12) 62%,
    transparent 100%
  );
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.14;
  background-image:
    repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(224, 122, 47, 0.35) 38px, rgba(224, 122, 47, 0.35) 40px),
    repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(92, 58, 78, 0.5) 38px, rgba(92, 58, 78, 0.5) 40px);
  pointer-events: none;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(1.5rem, 3.5vw, 2.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
  animation: ${fadeUp} 0.65s ease both;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.5rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${H.torch};
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2.2rem, 5.5vw, 4rem);
  font-weight: 700;
  line-height: 1.02;
  color: #fff;

  em {
    font-style: italic;
    color: ${H.candle};
  }
`;

const SelectionSection = styled.section`
  padding: clamp(3rem, 6vw, 5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${H.bone};
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const LeadText = styled.p`
  text-align: center;
  margin: 0 auto clamp(2.5rem, 5vw, 3.5rem);
  max-width: 560px;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.1rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  line-height: 1.55;
  color: rgba(20, 16, 31, 0.7);
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  max-width: 520px;
  margin: 0 auto;
`;

const TicketCard = styled.article`
  position: relative;
  background: #fff;
  border-radius: 18px;
  border: 1.5px solid ${p => (p.$featured ? H.torch : 'rgba(20,16,31,0.09)')};
  box-shadow: ${p =>
    p.$featured
      ? '0 8px 48px rgba(224,122,47,0.18), 0 2px 12px rgba(20,16,31,0.08)'
      : '0 4px 24px rgba(20,16,31,0.07)'};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.25s ease, transform 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${p =>
      p.$featured
        ? '0 14px 56px rgba(224,122,47,0.24), 0 4px 16px rgba(20,16,31,0.1)'
        : '0 12px 40px rgba(20,16,31,0.12)'};
  }
`;

const CardHeader = styled.div`
  background: ${p => (p.$dark ? H.ink : H.parchment)};
  padding: clamp(1.5rem, 3vw, 2.25rem) clamp(1.5rem, 3vw, 2rem);
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => (p.$dark ? 'rgba(224,122,47,0.18)' : 'rgba(224,122,47,0.12)')};
  border-radius: 12px;
  margin-bottom: 1.1rem;
`;

const CardTitle = styled.h2`
  margin: 0 0 0.45rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${p => (p.$light ? '#fff' : H.ink)};

  em {
    font-style: italic;
    color: ${H.torch};
  }
`;

const CardTagline = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  line-height: 1.55;
  color: ${p => (p.$light ? 'rgba(255,255,255,0.65)' : 'rgba(20,16,31,0.55)')};
`;

const CardBody = styled.div`
  padding: clamp(1.5rem, 3vw, 2rem) clamp(1.5rem, 3vw, 2rem);
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const IncludesList = styled.ul`
  list-style: none;
  margin: 0 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const IncludesItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.45;
  color: rgba(20, 16, 31, 0.72);

  &::before {
    content: '';
    flex-shrink: 0;
    margin-top: 0.22em;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${H.torch};
    background-image: url("data:image/svg+xml,%3Csvg width='8' height='6' viewBox='0 0 8 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 3l2 2 4-4' stroke='white' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 8px 6px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(20, 16, 31, 0.07);
  margin: 1.25rem 0;
`;

const BookBtn = styled.a`
  display: block;
  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  background: ${p => (p.$primary ? H.torch : H.ink)};
  color: #fff;
  box-shadow: ${p =>
    p.$primary
      ? '0 6px 24px rgba(224,122,47,0.38)'
      : '0 6px 24px rgba(20,16,31,0.25)'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin-top: 1.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${p =>
      p.$primary
        ? '0 10px 32px rgba(224,122,47,0.48)'
        : '0 10px 32px rgba(20,16,31,0.32)'};
  }
`;

const CardNote = styled.p`
  margin: 0.75rem 0 0;
  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  color: rgba(20, 16, 31, 0.38);
  letter-spacing: 0.04em;
`;

const SharedStrip = styled.div`
  margin-top: clamp(2rem, 4vw, 3rem);
  padding: clamp(1.5rem, 3vw, 2rem) clamp(1.5rem, 4vw, 2.5rem);
  background: ${H.ink};
  border-radius: 14px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 2rem;
`;

const SharedLabel = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${H.torch};
  white-space: nowrap;
`;

const SharedItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  flex: 1;
`;

const SharedPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.76rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.03em;

  &::before {
    content: '✓';
    color: ${H.torch};
    font-weight: 700;
    font-size: 0.7rem;
  }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: ${H.torch};
  margin-bottom: clamp(1.5rem, 3vw, 2rem);
  padding: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.75;
  }
`;

const HalloweenTickets = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Book Tickets — Belvoir&rsquo;s Enchanted Halloween Trail 2026 | Belvoir Castle</title>
        <meta
          name="description"
          content="Book Belvoir's Enchanted Halloween Trail tickets. Gardens & Playground entry includes the Halloween adventure trail, Animal Park, and Curse Breaker treat."
        />
        <link rel="canonical" href={getCanonicalUrl('halloween-belvoir-castle/tickets')} />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="Book Tickets — Belvoir's Enchanted Halloween Trail 2026" />
        <meta
          property="og:description"
          content="Gardens & Playground entry — Halloween trail, Animal Park and Curse Breaker treat included. Book online."
        />
        <meta property="og:url" content={getCanonicalUrl('halloween-belvoir-castle/tickets')} />
        <meta property="og:image" content={HERO_IMG} />
      </Helmet>

      <Page>
        <HeroWrap>
          <HeroImg src={HERO_IMG} alt="Belvoir's Enchanted Halloween Trail" />
          <HeroOverlay />
          <HeroPattern />
          <HeroContent>
            <HeroKicker>Belvoir&rsquo;s Enchanted Halloween Trail · October School Holidays 2026</HeroKicker>
            <HeroTitle>
              Book Your <em>Visit</em>
            </HeroTitle>
          </HeroContent>
        </HeroWrap>

        <SelectionSection>
          <Inner>
            <BackLink type="button" onClick={() => navigate('/halloween-belvoir-castle')}>
              ← Back to Enchanted Halloween Trail
            </BackLink>

            <LeadText>
              Tickets include the Halloween adventure trail, Animal Park, and a treat for every Curse Breaker who completes the mission.
              Book online to secure your dates.
            </LeadText>

            <CardGrid>
              <TicketCard $featured>
                <CardHeader $dark>
                  <CardIcon $dark>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={H.torch} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a7 7 0 0 0-7 7c0 4 7 13 7 13s7-9 7-13a7 7 0 0 0-7-7z" />
                      <circle cx="12" cy="9" r="2.5" />
                    </svg>
                  </CardIcon>
                  <CardTitle $light>
                    Gardens &amp; <em>Playground</em>
                  </CardTitle>
                  <CardTagline $light>
                    A full outdoor day — gardens, adventure playground, and the Halloween trail.
                  </CardTagline>
                </CardHeader>

                <CardBody>
                  <IncludesList>
                    <IncludesItem>Formal gardens &amp; grounds</IncludesItem>
                    <IncludesItem>Adventure Playground</IncludesItem>
                    <IncludesItem>Halloween adventure trail &amp; map</IncludesItem>
                    <IncludesItem>Animal Park — no extra ticket</IncludesItem>
                    <IncludesItem>Treat &amp; Curse Breaker title for every child who completes the trail</IncludesItem>
                  </IncludesList>

                  <Divider />

                  <BookBtn
                    $primary
                    href={GARDENS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Gardens Tickets
                  </BookBtn>
                  <CardNote>Opens secure booking at belvoircastle.com</CardNote>
                </CardBody>
              </TicketCard>
            </CardGrid>

            <SharedStrip>
              <SharedLabel>Tickets include</SharedLabel>
              <SharedItems>
                <SharedPill>Halloween Adventure Trail</SharedPill>
                <SharedPill>Animal Park</SharedPill>
                <SharedPill>Curse Breaker Treat</SharedPill>
                <SharedPill>Adventure Playground</SharedPill>
              </SharedItems>
              <img
                src={PEACOCK}
                alt=""
                style={{ height: 38, width: 'auto', opacity: 0.55, flexShrink: 0 }}
              />
            </SharedStrip>

            <p
              style={{
                margin: 'clamp(1.5rem, 3vw, 2rem) auto 0',
                maxWidth: '40rem',
                padding: '1rem 1.15rem',
                borderRadius: 10,
                border: '1px solid rgba(224,122,47,0.28)',
                background: 'rgba(224,122,47,0.08)',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '0.88rem',
                lineHeight: 1.55,
                color: 'rgba(20,16,31,0.72)',
                textAlign: 'center',
              }}
            >
              <strong style={{ color: H.ink }}>Please note:</strong> HHA Members and RHS Members are required to pay £4.50 to enter the Animal Park. This can be added when booking online.
            </p>

            <p
              style={{
                textAlign: 'center',
                marginTop: '1.5rem',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '0.76rem',
                color: 'rgba(20,16,31,0.38)',
                letterSpacing: '0.04em',
              }}
            >
              October School Holidays 2026 · Silly, not scary · Online booking recommended
            </p>
          </Inner>
        </SelectionSection>

        <Footer />
      </Page>
    </>
  );
};

export default HalloweenTickets;
